import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./common";

export function TestimonialCard({ item, editing, onEdit, onCloseEdit, onSaved, onRemove, onToggleVisible }) {
  const [form, setForm] = useState({
    display_name: item.display_name || "",
    display_role: item.display_role || "",
    display_entity: item.display_entity || "",
    display_quote: item.display_quote || "",
    sort_order: item.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      display_name: item.display_name || "",
      display_role: item.display_role || "",
      display_entity: item.display_entity || "",
      display_quote: item.display_quote || "",
      sort_order: item.sort_order ?? 0,
    });
  }, [item.id, item.display_name, item.display_role, item.display_entity, item.display_quote, item.sort_order]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/landing/testimonials/${item.id}`, form);
      toast.success("Testimonio actualizado");
      onCloseEdit();
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-gray-200 rounded-sm">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-300">
              Score {item.nps_score ?? "—"}
            </Badge>
            <Badge className="rounded-sm bg-gray-100 text-gray-700 border border-gray-300">
              {item.nivel === "nivel1" ? "Diagnóstico" : "Kit Documental"}
            </Badge>
            {item.is_visible ? (
              <Badge className="rounded-sm bg-primary/10 text-primary border border-primary/30">
                Visible
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-gray-50 text-gray-500 border border-gray-300">
                Oculto
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={onToggleVisible}
              className="p-1.5 text-gray-500 hover:text-primary"
              title={item.is_visible ? "Ocultar del landing" : "Mostrar en el landing"}
              data-testid={`btn-toggle-${item.id}`}
            >
              {item.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            {!editing && (
              <button
                onClick={onEdit}
                className="p-1.5 text-gray-500 hover:text-primary"
                title="Editar"
                data-testid={`btn-edit-${item.id}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onRemove}
              className="p-1.5 text-gray-500 hover:text-destructive"
              title="Eliminar del landing"
              data-testid={`btn-remove-${item.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {editing ? (
          <div className="space-y-3" data-testid={`edit-form-${item.id}`}>
            <Field label="Nombre a mostrar" required>
              <input
                type="text"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="input-base"
                maxLength={80}
              />
            </Field>
            <Field label="Cargo / rol">
              <input
                type="text"
                value={form.display_role}
                onChange={(e) => setForm({ ...form, display_role: e.target.value })}
                className="input-base"
                maxLength={120}
                placeholder="Ej: Asesor jurídico"
              />
            </Field>
            <Field label="Entidad (opcional)">
              <input
                type="text"
                value={form.display_entity || ""}
                onChange={(e) => setForm({ ...form, display_entity: e.target.value })}
                className="input-base"
                maxLength={120}
                placeholder="Ej: Alcaldía de ..."
              />
            </Field>
            <Field label="Cita / opinión">
              <textarea
                rows={4}
                value={form.display_quote || ""}
                onChange={(e) => setForm({ ...form, display_quote: e.target.value })}
                className="input-base resize-none"
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">
                Original NPS: <em className="text-gray-700">{item.nps_comment_original || "—"}</em>
              </p>
            </Field>
            <Field label="Orden (sort_order)">
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                className="input-base w-28"
              />
            </Field>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={saving} className="rounded-sm bg-primary" data-testid={`btn-save-${item.id}`}>
                {saving ? "Guardando…" : "Guardar"}
              </Button>
              <Button variant="outline" onClick={onCloseEdit} className="rounded-sm border-gray-300" data-testid={`btn-cancel-${item.id}`}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <blockquote className="text-sm text-gray-800 italic border-l-2 border-primary pl-3">
              &ldquo;{item.display_quote || "(sin cita)"}&rdquo;
            </blockquote>
            <div className="text-sm">
              <div className="font-medium text-gray-900">{item.display_name || "—"}</div>
              <div className="text-xs text-gray-600">
                {item.display_role}{item.display_entity ? ` · ${item.display_entity}` : ""}
              </div>
            </div>
            <div className="text-[11px] text-gray-400 flex flex-wrap gap-x-3 pt-1 border-t border-gray-100">
              <span>orden: {item.sort_order}</span>
              {item.created_at && <span>creado: {new Date(item.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
